import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin } from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-white/10 pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/assets/navbar-logo.png"
                alt="White Glove Event Concierge"
                className="h-14 w-auto"
              />
              <div>
                <p className="font-heading text-white text-base leading-tight tracking-wide">White Glove</p>
                <p className="text-white text-[10px] uppercase tracking-[0.25em]">Event Concierge</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Strategic event guidance with precision, clarity, and intention.
            </p>
            {/* Social + email icons */}
            <div className="flex items-center gap-3">
              <a
                href="mailto:info@whitegloveeventconcierge.com"
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                aria-label="Email us"
                data-testid="footer-email-icon"
              >
                <Mail size={16} />
              </a>
              <a
                href="https://www.instagram.com/wgeventconcierge?igsh=MWQ4YjR5bm15bjZ5eQ%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61576514856263"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://www.linkedin.com/company/white-glove-event-concierge/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Contact — moved next to Brand so the email fits on one line */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <Mail size={16} className="text-[#D4AF37] flex-shrink-0" />
                <a href="mailto:info@whitegloveeventconcierge.com" className="hover:text-white transition-colors whitespace-nowrap" data-testid="footer-email-link">
                  info@whitegloveeventconcierge.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <Phone size={16} className="text-[#D4AF37] flex-shrink-0" />
                <a href="tel:+16156690576" className="hover:text-white transition-colors" data-testid="footer-phone-link">
                  615.669.0576
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <MapPin size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span>Nashville, Tennessee</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Explore</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/consulting" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-consulting">Consulting</Link>
              </li>
              <li>
                <Link to="/design" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-design">Design</Link>
              </li>
              <li>
                <Link to="/portfolio" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-portfolio">Portfolio</Link>
              </li>
              <li>
                <Link to="/blog" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-blog">Blog</Link>
              </li>
              <li>
                <Link to="/about" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-about">About</Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-link-contact">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-widest text-[#D4AF37] mb-6">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/consulting" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-service-strategy">Event Strategy</Link>
              </li>
              <li>
                <Link to="/consulting" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-service-vendor">Vendor Alignment</Link>
              </li>
              <li>
                <Link to="/consulting" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-service-advisory">Advisory Support</Link>
              </li>
              <li>
                <Link to="/design" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-service-design">Design Direction</Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/60 hover:text-white transition-colors text-sm" data-testid="footer-service-strategy-session">Strategy Session</Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs text-center md:text-left">
            © {new Date().getFullYear()} <span className="font-heading">White Glove</span> Event Concierge. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/privacy" className="text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-terms-link">
              Terms of Use
            </Link>
            <Link to="/login" className="text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-admin-link">
              Client & Admin Login
            </Link>
          </div>
        </div>

        {/* Legal microcopy */}
        <div className="mt-6 pt-6 border-t border-white/5">
          <p className="text-white/35 text-[11px] leading-relaxed text-center max-w-3xl mx-auto" data-testid="footer-legal-microcopy">
            White Glove Event Concierge provides strategic advisory and coordination support. We do not directly provide vendor services and are not responsible for third-party execution.
          </p>
        </div>
      </div>
    </footer>
  );
};
